import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import {
  PATH_CLOSING_PORTAL,
  PATH_CLOSING_PORTAL_LOGIN,
} from 'app/configs/routesConfig/closingPortal';
import { HomePagePaths } from 'app/configs/routesConfig/homePage';
import { TpoCertificationPaths } from 'app/configs/routesConfig/tpoCertification';
import { TpoPortalPaths } from 'app/configs/routesConfig/tpoPortal';
import storage from 'app/services/storage';
import _ from 'lodash';

import { PATH_AUTO_LOGIN, PATH_LOGIN, PATH_SSO_LOGIN } from 'constants/authPaths';
import { LS_REDIRECT_URL } from 'constants/localStorage';
import { START_PAGE_PATH } from 'constants/navigation';
import { checkIsPathAllowedWhileCertificationPending } from 'utils/certificationAccess';
import { checkIsErrorPagePath } from 'utils/navigation';
import { hasPermission } from 'utils/permissions';

import {
  getMasterPermissions,
  getMasterRole,
  selectIsSubCompanyCertificationPending,
  selectIsTitleCompanyUser,
} from 'store/auth/selectors';

const useAuthorization = route => {
  const history = useHistory();
  const { pathname, search } = useLocation();

  const masterRole = useSelector(getMasterRole);
  const permissions = useSelector(getMasterPermissions);
  const isCertificationPending = useSelector(selectIsSubCompanyCertificationPending);
  const isTitleCompanyUser = useSelector(selectIsTitleCompanyUser);

  const [isAccessGranted, setIsAccessGranted] = useState(true);

  const isCertificationBlocked =
    isCertificationPending && !checkIsPathAllowedWhileCertificationPending(pathname);

  // A route can only be judged against the permission set when it actually gates on a page code;
  // guest-only / auth-agnostic / role-agnostic / skip-permission routes are decided without it.
  const routeNeedsResolvedPermissions =
    !!route &&
    !!route.pageCode &&
    !route.skipPermissionCheck &&
    !route.isAuthAgnostic &&
    !route.isGuestOnly &&
    !route.isAllowedWithoutRoles;

  // A signed-in member's permissions load asynchronously. On a cold load of a permission-gated
  // deep route the first synchronous check runs before they arrive — treating the route as denied
  // (403) or letting it fall through to not-found. Hold access unresolved while the member has a
  // role but no permissions yet, so the layout renders a loading state and re-checks once they land.
  const isResolvingPermissions =
    routeNeedsResolvedPermissions &&
    _.isArray(masterRole) &&
    masterRole.length > 0 &&
    _.isNil(permissions);

  const checkAccessToPage = useCallback(() => {
    if (isResolvingPermissions) {
      return;
    }

    // A title-company user's whole app is the Closing Portal — deny any other route outright
    // instead of falling through to the normal permission check below.
    const isClosingPortalRoute = pathname.startsWith(PATH_CLOSING_PORTAL);

    if (isTitleCompanyUser && !isClosingPortalRoute) {
      setIsAccessGranted(false);

      return;
    }

    const isPageAccessGranted = route
      ? hasPermission({
          pageCode: route.pageCode,
          permissions,
          masterRole,
          isGuestOnly: route.isGuestOnly,
          isAllowedWithoutRoles: route.isAllowedWithoutRoles,
          skipPermissionCheck: route.skipPermissionCheck,
          isAuthAgnostic: route.isAuthAgnostic,
        })
      : true;

    const isAccess = !_.isNull(masterRole) && isPageAccessGranted;

    // Error pages are never a destination: the catch-all route redirects a logged-out tab onto the 404
    // page, and capturing it here would land the user on that 404 again right after they sign in.
    const isRedirectExcludedPath =
      checkIsErrorPagePath(pathname) ||
      [
        HomePagePaths.home.path,
        PATH_AUTO_LOGIN,
        START_PAGE_PATH,
        // The Command Center home is the default landing — don't capture a transient
        // unauthorized hit on it (or its aliases) into LS_REDIRECT_URL and replay it.
        TpoPortalPaths.commandCenter.path,
        TpoPortalPaths.commandCenterAlias.path,
        TpoPortalPaths.commandCenterLegacyAlias.path,
      ].includes(pathname);

    // A path the pending certification blocks must never be captured for replay: the user is being
    // bounced to the certification flow, and storing it would send them straight back on the next
    // resolve.
    if (isAccess && !isRedirectExcludedPath && !isCertificationBlocked) {
      storage.setItem(LS_REDIRECT_URL, pathname);
    }

    if (
      !isAccess &&
      !isRedirectExcludedPath &&
      !isCertificationBlocked &&
      ![PATH_LOGIN, PATH_SSO_LOGIN].includes(pathname)
    ) {
      storage.setItem(LS_REDIRECT_URL, pathname);
    }

    setIsAccessGranted(isPageAccessGranted && !isCertificationBlocked);
  }, [
    pathname,
    masterRole,
    permissions,
    route,
    isResolvingPermissions,
    isCertificationBlocked,
    isTitleCompanyUser,
  ]);

  const redirectUser = useCallback(() => {
    const storedRedirectUrl = storage.getItem(LS_REDIRECT_URL);
    // A value stored before error pages were excluded can still be sitting in a user's storage.
    const redirectUrl =
      storedRedirectUrl && !checkIsErrorPagePath(storedRedirectUrl) ? storedRedirectUrl : '/';

    // Master is guest - Redirect to Login Page. Preserve the query string: an emailed activation
    // link (`?requestId=&code=`) lands a guest on the bare domain root, and Login's own
    // window.location.search parsing would otherwise never see the params once this redirect has
    // fired.
    if (_.isNull(masterRole)) {
      if (pathname.startsWith(PATH_CLOSING_PORTAL)) {
        history.push({ pathname: PATH_CLOSING_PORTAL_LOGIN });

        return;
      }

      history.push({ pathname: PATH_LOGIN, search });

      return;
    }

    // Sub-company still awaiting certification — the certification flow is the whole app for them.
    // Checked ahead of the no-role branch: that branch pushes '/', which redirects to the Command
    // Center and would bounce straight back here.
    // replace, not push: a pending user bounces off every blocked path, and pushing would grow the
    // history stack one entry per bounce and leave Back landing on a path that immediately bounces
    // again.
    if (isCertificationPending) {
      history.replace({ pathname: TpoCertificationPaths.tpoCertification.path });

      return;
    }

    // User is member without role
    if (masterRole?.length === 0) {
      history.push({ pathname: '/' });

      return;
    }

    // Title-company user — the Closing Portal is the whole app for them.
    if (isTitleCompanyUser) {
      history.push({ pathname: PATH_CLOSING_PORTAL });

      return;
    }

    // User is member - User must be on unauthorized page or just logged in - Redirect to home or redirectUrl
    storage.removeItem(LS_REDIRECT_URL);
    history.push({ pathname: redirectUrl });
  }, [history, masterRole, pathname, search, isCertificationPending, isTitleCompanyUser]);

  useLayoutEffect(() => {
    checkAccessToPage();
  }, [checkAccessToPage]);

  useLayoutEffect(() => {
    if (isAccessGranted || isResolvingPermissions) {
      return;
    }

    redirectUser();
  }, [isAccessGranted, isResolvingPermissions, redirectUser]);

  return useMemo(
    () => ({ isAccessGranted, isResolvingPermissions }),
    [isAccessGranted, isResolvingPermissions],
  );
};

export default useAuthorization;
